/** Device categories stored on analytics events. */
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

/** Paths that should never be tracked (admin / auth). */
export function shouldSkipAnalyticsPath(pathname: string) {
    if (!pathname) return true;
    if (pathname.startsWith('/admin')) return true;
    if (pathname.startsWith('/sign-')) return true;
    if (pathname.startsWith('/recover')) return true;
    if (pathname.includes('/preview')) return true;
    return false;
}

const DEVICE_KEY = 'an_device';
const REFERRER_KEY = 'an_ref';

function isBrowser() {
    return typeof window !== 'undefined' && typeof sessionStorage !== 'undefined';
}

/** Derive device type from user agent (no external dependency). */
export function detectDeviceType(userAgent: string): DeviceType {
    const ua = userAgent || '';
    if (/iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i.test(ua)) {
        return 'tablet';
    }
    if (
        /Mobile|iPhone|iPod|Android.*Mobile|BlackBerry|IEMobile|Opera Mini/i.test(
            ua,
        )
    ) {
        return 'mobile';
    }
    return 'desktop';
}

/**
 * Normalize document.referrer into a stable source key.
 * Empty or same-site referrers become `direct`.
 */
export function normalizeReferrerSource(
    referrer: string,
    siteHost: string,
): string {
    const trimmed = referrer.trim();
    if (!trimmed) return 'direct';

    try {
        const url = new URL(trimmed);
        const host = url.hostname.replace(/^www\./i, '').toLowerCase();
        const site = siteHost.replace(/^www\./i, '').toLowerCase();
        if (!host || host === site) return 'direct';

        if (host.includes('google.')) return 'google';
        if (
            host === 'facebook.com' ||
            host.endsWith('.facebook.com') ||
            host === 'fb.com' ||
            host.endsWith('.fb.com')
        ) {
            return 'facebook';
        }
        if (host.includes('instagram.')) return 'instagram';
        if (
            host === 'twitter.com' ||
            host.endsWith('.twitter.com') ||
            host === 'x.com' ||
            host.endsWith('.x.com') ||
            host === 't.co'
        ) {
            return 'twitter';
        }
        if (host.includes('whatsapp.')) return 'whatsapp';
        if (host === 't.me' || host.endsWith('.telegram.org')) return 'telegram';
        if (host.includes('bing.')) return 'bing';
        if (host.includes('yahoo.')) return 'yahoo';
        if (host.includes('youtube.') || host === 'youtu.be') return 'youtube';
        if (host.includes('linkedin.')) return 'linkedin';

        return host.slice(0, 128);
    } catch {
        return 'direct';
    }
}

/** Hebrew labels for known referrer keys in admin reports. */
export const REFERRER_LABELS_HE: Record<string, string> = {
    direct: 'ישיר',
    google: 'Google',
    facebook: 'Facebook',
    instagram: 'Instagram',
    twitter: 'X / Twitter',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    bing: 'Bing',
    yahoo: 'Yahoo',
    youtube: 'YouTube',
    linkedin: 'LinkedIn',
};

/** Hebrew labels for device types in admin reports. */
export const DEVICE_LABELS_HE: Record<string, string> = {
    mobile: 'נייד',
    tablet: 'טאבלט',
    desktop: 'מחשב',
};

export function hebrewReferrerLabel(key: string | null): string {
    if (!key) return 'לא ידוע';
    return REFERRER_LABELS_HE[key] ?? key;
}

export function hebrewDeviceLabel(key: string | null): string {
    if (!key) return 'לא ידוע';
    return DEVICE_LABELS_HE[key] ?? key;
}

/** Cached device type for the tab session. */
export function getSessionDeviceType(): DeviceType | null {
    if (!isBrowser()) return null;
    try {
        const cached = sessionStorage.getItem(DEVICE_KEY);
        if (
            cached === 'mobile' ||
            cached === 'tablet' ||
            cached === 'desktop'
        ) {
            return cached;
        }
        const device = detectDeviceType(navigator.userAgent);
        sessionStorage.setItem(DEVICE_KEY, device);
        return device;
    } catch {
        return detectDeviceType(
            typeof navigator !== 'undefined' ? navigator.userAgent : '',
        );
    }
}

/**
 * Entry referrer for the tab session (first external source).
 * Stays stable across SPA navigations.
 */
export function getSessionReferrerSource(): string | null {
    if (!isBrowser()) return null;
    try {
        const cached = sessionStorage.getItem(REFERRER_KEY);
        if (cached !== null) {
            return cached || null;
        }
        const source = normalizeReferrerSource(
            document.referrer,
            window.location.hostname,
        );
        sessionStorage.setItem(REFERRER_KEY, source);
        return source;
    } catch {
        return normalizeReferrerSource(
            typeof document !== 'undefined' ? document.referrer : '',
            typeof window !== 'undefined' ? window.location.hostname : '',
        );
    }
}
